import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AgentDefinition, ChatMessage, Conversation, FeedbackRating, ToolCall } from '../types';
import { useConversations } from '../hooks/useConversations';
import { useAgents } from '../hooks/useAgents';
import { useAgentOptions } from '../hooks/useAgentOptions';
import { useStreamingChat, PersistedTurn } from '../hooks/useStreamingChat';
import { useAccessToken } from '@app/auth';
import { deleteConversation, fetchMessages, submitFeedback } from '../api/conversations';
import { dbConvToLocal, dtoToChatMessage } from '../utils/chatMessages';
import { ConversationSidebar } from './ConversationSidebar';
import { ChatHeader } from './ChatHeader';
import { MessageThread } from './MessageThread';
import { ChatInput } from './ChatInput';
import { ChatToolbar } from './ChatToolbar';
import '../styles/ai-chatbots.css';

const MESSAGE_PAGE_SIZE = 30;

/** Past this distance from the bottom the user is reading history, so don't auto-scroll. */
const AUTOSCROLL_STICKY_PX = 400;

interface MessagePaging {
  hasMore: boolean;
  nextCursor: string | null;
  loadingOlder: boolean;
}

export const ChatPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const threadRef = useRef<HTMLDivElement>(null);
  const getToken = useAccessToken();

  const params = new URLSearchParams(location.search);
  const initialAgentId = params.get('agentId');
  const initialConversationId = params.get('conversationId');

  const { agents } = useAgents();
  const { conversations: dbConvDtos, loading: convsLoading, refetch } = useConversations();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const loadedConvsRef = useRef<Set<string>>(new Set());
  const [paging, setPaging] = useState<Record<string, MessagePaging>>({});
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [initialLoadedFor, setInitialLoadedFor] = useState<string | null>(null);
  const scrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  // A ref, not the `loadingOlder` flag: scroll events fire many times before a
  // state update flushes, so the flag would still read false and duplicate fetches.
  const loadingOlderRef = useRef(false);

  // ── Merge DB conversation list with already-loaded messages ──
  useEffect(() => {
    if (convsLoading) return;
    setConversations((prev) => {
      const prevMessages = new Map(prev.map((c) => [c.id, c.messages]));
      return dbConvDtos.map((d) => dbConvToLocal(d, prevMessages.get(d.id) ?? []));
    });
  }, [dbConvDtos, convsLoading]);

  // ── Derive initial active conversation from URL ──
  useEffect(() => {
    if (convsLoading || conversations.length === 0 || activeConversationId !== null) return;
    if (initialConversationId) {
      setActiveConversationId(initialConversationId);
      return;
    }
    if (initialAgentId) {
      const candidates = conversations
        .filter((c) => c.lastAgentId === initialAgentId)
        .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
      setActiveConversationId(candidates[0]?.id ?? null);
    }
  }, [conversations, convsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeConversation = conversations.find((c) => c.id === activeConversationId) ?? null;

  // ── Load the newest page of messages on first selection of a conversation ──
  useEffect(() => {
    const convId = activeConversationId;
    if (!convId || loadedConvsRef.current.has(convId)) return;
    let cancelled = false;

    // Claimed up front so a re-run can't double-fetch; released on failure so
    // reselecting retries.
    loadedConvsRef.current.add(convId);
    setMessagesError(null);

    fetchMessages(getToken, convId, { limit: MESSAGE_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        const chatMsgs = page.messages.map(dtoToChatMessage);
        // Merge by id rather than replace: a send that lands while this fetch is
        // in flight has already appended an optimistic or persisted turn, and
        // those are newer than anything in this page.
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== convId) return c;
            const fetched = new Set(chatMsgs.map((m) => m.id));
            const localOnly = c.messages.filter((m) => !fetched.has(m.id));
            return { ...c, messages: [...chatMsgs, ...localOnly] };
          }),
        );
        setPaging((prev) => ({
          ...prev,
          [convId]: { hasMore: page.hasMore, nextCursor: page.nextCursor, loadingOlder: false },
        }));
        setInitialLoadedFor(convId);
      })
      .catch(() => {
        if (cancelled) return;
        loadedConvsRef.current.delete(convId);
        setMessagesError('Could not load this conversation\'s history. Reselect it to retry.');
      });

    return () => { cancelled = true; };
  }, [activeConversationId, getToken]);

  // ── Fetch the previous page and prepend it ──
  const handleLoadOlder = useCallback(async () => {
    const convId = activeConversationId;
    if (!convId) return;
    const current = paging[convId];
    if (!current?.hasMore || !current.nextCursor) return;
    if (loadingOlderRef.current) return;
    loadingOlderRef.current = true;

    // Captured before the prepend so the layout effect below can hold position.
    const el = threadRef.current;
    scrollRestoreRef.current = el
      ? { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop }
      : null;

    setMessagesError(null);
    setPaging((prev) => ({ ...prev, [convId]: { ...prev[convId], loadingOlder: true } }));

    try {
      const page = await fetchMessages(getToken, convId, {
        limit: MESSAGE_PAGE_SIZE,
        before: current.nextCursor,
      });
      const older = page.messages.map(dtoToChatMessage);
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== convId) return c;
          const seen = new Set(c.messages.map((m) => m.id));
          const fresh = older.filter((m) => !seen.has(m.id));
          return { ...c, messages: [...fresh, ...c.messages] };
        }),
      );
      setPaging((prev) => ({
        ...prev,
        [convId]: { hasMore: page.hasMore, nextCursor: page.nextCursor, loadingOlder: false },
      }));
    } catch {
      scrollRestoreRef.current = null;
      setPaging((prev) => ({ ...prev, [convId]: { ...prev[convId], loadingOlder: false } }));
      setMessagesError('Could not load earlier messages. Try again.');
    } finally {
      loadingOlderRef.current = false;
    }
  }, [activeConversationId, paging, getToken]);

  // ── Hold the reading position when an older page is prepended (pre-paint) ──
  useLayoutEffect(() => {
    const restore = scrollRestoreRef.current;
    if (!restore) return;
    scrollRestoreRef.current = null;
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = restore.scrollTop + (el.scrollHeight - restore.scrollHeight);
  }, [activeConversation?.messages]);

  // ── Jump to newest on open: both switching to a loaded conversation and when
  // a freshly-selected one's first page lands ──
  useLayoutEffect(() => {
    if (!activeConversationId || !loadedConvsRef.current.has(activeConversationId)) return;
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [activeConversationId, initialLoadedFor]);

  // ── Selected agent / prompt mode / model ──
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinition | null>(
    () => agents.find((a) => a.id === initialAgentId) ?? null,
  );
  const [selectedPromptMode, setSelectedPromptMode] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const { options, loading: optionsLoading, promptsError, modelsError } = useAgentOptions(selectedAgent);

  useEffect(() => {
    if (activeConversation) {
      setSelectedAgent(agents.find((a) => a.id === activeConversation.lastAgentId) ?? null);
    }
  }, [activeConversationId, agents]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedAgent || !initialAgentId) return;
    const found = agents.find((a) => a.id === initialAgentId);
    if (found) setSelectedAgent(found);
  }, [agents]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedPromptMode(options.promptModes[0]?.id ?? null);
    setSelectedModel(options.defaultModel ?? options.supportedModels[0]?.id ?? null);
  }, [options]);

  // ── Streaming send (state + SSE event loop lives in the hook) ──
  const onConversationCreated = useCallback((newConv) => {
    loadedConvsRef.current.add(newConv.id);
    setConversations((prev) => [dbConvToLocal(newConv), ...prev]);
    setPaging((prev) => ({ ...prev, [newConv.id]: { hasMore: false, nextCursor: null, loadingOlder: false } }));
    setActiveConversationId(newConv.id);
    history.replace(`/ai-chat-agents/chat?conversationId=${newConv.id}`);
  }, [history]);

  const onOptimisticInsert = useCallback((convId: string, msg: ChatMessage) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === convId
          ? { ...c, messages: [...c.messages, msg], lastMessageAt: new Date().toISOString() }
          : c,
      ),
    );
  }, []);

  const onPersisted = useCallback(({ convId, optimisticUserId, userMessage, assistantMessage, toolCalls }: PersistedTurn) => {
    const realUser = dtoToChatMessage(userMessage);
    const assistantFromDto = dtoToChatMessage(assistantMessage);
    const realAssistant: ChatMessage = {
      ...assistantFromDto,
      // Prefer tool calls collected live during streaming; otherwise fall back
      // to whatever the persisted DTO carries (non-streaming agents return them
      // here).
      toolCalls:
        toolCalls && toolCalls.length > 0
          ? toolCalls
          : assistantFromDto.toolCalls && assistantFromDto.toolCalls.length > 0
          ? assistantFromDto.toolCalls
          : undefined,
    };
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== convId) return c;
        const withoutOptimistic = c.messages.filter((m) => m.id !== optimisticUserId);
        const isFirst = withoutOptimistic.length === 0;
        return {
          ...c,
          title: isFirst ? realUser.content.slice(0, 40) : c.title,
          lastAgentId: assistantMessage.agentId ?? c.lastAgentId,
          lastMessageAt: assistantMessage.createdAt,
          messages: [...withoutOptimistic, realUser, realAssistant],
        };
      }),
    );
  }, []);

  const onError = useCallback((convId: string | null, errorMsg: ChatMessage) => {
    if (!convId) return;
    setConversations((prev) =>
      prev.map((c) => (c.id === convId ? { ...c, messages: [...c.messages, errorMsg] } : c)),
    );
  }, []);

  const stream = useStreamingChat({
    getToken, onConversationCreated, onOptimisticInsert, onPersisted, onError,
  });

  // Keyed on the last message id, not count, so prepending an older page doesn't
  // scroll away from what the user just asked to see.
  const lastMessageId = activeConversation?.messages[activeConversation.messages.length - 1]?.id;
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > AUTOSCROLL_STICKY_PX) return;
    el.scrollTop = el.scrollHeight;
  }, [lastMessageId, stream.streamingContent]);

  // ── Handlers ──
  const handleSelectConversation = useCallback((id: string) => {
    setMessagesError(null);
    setActiveConversationId(id);
    history.replace(`/ai-chat-agents/chat?conversationId=${id}`);
  }, [history]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    try { await deleteConversation(getToken, id); } catch { /* ignore */ }
    refetch();
    loadedConvsRef.current.delete(id);
    setPaging((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setConversations((prev) => prev.filter((c) => c.id !== id));
    setActiveConversationId((current) => {
      if (current !== id) return current;
      const remaining = conversations.filter((c) => c.id !== id);
      return remaining[0]?.id ?? null;
    });
  }, [conversations, getToken, refetch]);

  const handleNewConversation = useCallback(() => {
    setActiveConversationId(null);
    history.replace('/ai-chat-agents/chat');
  }, [history]);

  const handleFeedback = useCallback(async (messageId: string, rating: FeedbackRating) => {
    if (!activeConversationId) return;
    try {
      const fb = await submitFeedback(getToken, activeConversationId, messageId, rating);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === activeConversationId
            ? { ...c, messages: c.messages.map((m) => (m.id === messageId ? { ...m, feedback: fb } : m)) }
            : c,
        ),
      );
    } catch {
      // swallow — UI stays unchanged, user can retry
    }
  }, [activeConversationId, getToken]);

  const handleSend = useCallback((text: string) => {
    if (!selectedAgent) return;
    if (selectedAgent.mode !== 'portal-chat' || !selectedAgent.endpoints?.chatUrl) return;
    // Unconditional: the sticky guard would otherwise strand the viewport in history.
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    stream.send({
      text,
      agent: selectedAgent,
      conversationId: activeConversationId,
      promptMode: selectedPromptMode,
      model: selectedModel,
    });
  }, [stream, selectedAgent, activeConversationId, selectedPromptMode, selectedModel]);

  return (
    <div className="chat-page">
      <ConversationSidebar
        conversations={conversations}
        agents={agents}
        activeConversationId={activeConversationId}
        onSelect={handleSelectConversation}
        onNew={handleNewConversation}
        onDelete={handleDeleteConversation}
      />
      <div className="chat-main">
        <ChatHeader agent={selectedAgent} />
        {messagesError && (
          <div className="chat-thread__error" role="status">
            {messagesError}
          </div>
        )}
        <MessageThread
          messages={activeConversation?.messages ?? []}
          agents={agents}
          threadRef={threadRef}
          sending={stream.sendingMsg}
          streamingContent={stream.streamingContent}
          streamingElapsed={stream.streamingElapsed}
          streamingThinking={stream.streamingThinking}
          streamingAgentId={stream.streamingAgentId}
          streamingTools={stream.streamingTools}
          onFeedback={handleFeedback}
          hasMore={activeConversationId ? paging[activeConversationId]?.hasMore ?? false : false}
          loadingOlder={activeConversationId ? paging[activeConversationId]?.loadingOlder ?? false : false}
          onLoadOlder={handleLoadOlder}
        />
        <div className="chat-composer">
          <div className="chat-composer__card">
            <ChatToolbar
              agent={selectedAgent}
              agents={agents}
              options={options}
              optionsLoading={optionsLoading}
              promptsError={promptsError}
              modelsError={modelsError}
              selectedPromptMode={selectedPromptMode}
              selectedModel={selectedModel}
              conversationStarted={(activeConversation?.messages.length ?? 0) > 0}
              onAgentChange={setSelectedAgent}
              onPromptModeChange={setSelectedPromptMode}
              onModelChange={setSelectedModel}
            />
            <ChatInput
              onSend={handleSend}
              disabled={stream.sendingMsg}
              disabledReason={
                !selectedAgent
                  ? 'Select an agent to start chatting'
                  : selectedAgent.mode === 'external-link'
                  ? 'This agent opens in a new tab — use the link in the header'
                  : selectedAgent.mode === 'planned'
                  ? 'This agent is coming soon'
                  : !selectedAgent.endpoints?.chatUrl
                  ? 'Agent misconfigured — chat URL missing'
                  : undefined
              }
            />
          </div>
          <p className="chat-composer__hint">Press Enter to send · Shift+Enter for new line</p>
        </div>
      </div>
    </div>
  );
};
